// Mandatory-reload authorization — security plan phase 4
// (docs/plans/2026-09-19-login-and-action-authorization.md, finding F4).
//
// The `station:broadcast` realtime channel is public: anyone holding the publishable key (it
// ships in the bundle) can send `mandatory_reload` and, before this, every tablet on the floor
// reloaded mid-service. The broadcast is now only a hint. `bar-admin-broadcast-reload` (manager+)
// records each real mandatory reload in `bar_manual.audit_log` before sending, and
// `bar-admin-get-app-settings` returns the latest record with the server's clock. A tablet
// reloads only when that record is fresh and is not one it has already applied — so a forged
// event does nothing, and replaying a real one does nothing twice.
//
// Station auth is its own customer token, not Supabase Auth, so a Realtime private channel
// (which authorizes on a Supabase JWT) was not an option; see DECISIONS.md 2026-09-19.

export interface MandatoryReloadMarker {
  id: string;
  at: string; // ISO timestamp of the audit row
}

/** A real broadcast reaches tablets within seconds; two minutes covers the coalescing delay. */
export const RELOAD_FRESH_MS = 120_000;
/** Server and database clocks may differ slightly; a marker this far "in the future" still counts. */
export const RELOAD_CLOCK_SKEW_MS = 5_000;
/** At most one server check per tablet per window, however many events arrive. */
export const RELOAD_CHECK_WINDOW_MS = 10_000;

export function shouldApplyMandatoryReload(
  marker: MandatoryReloadMarker | null | undefined,
  serverNow: string | null | undefined,
  lastAppliedId: string | null,
): boolean {
  if (!marker || typeof marker.id !== "string" || !marker.id) return false;
  if (marker.id === lastAppliedId) return false;
  const at = Date.parse(marker.at);
  const now = Date.parse(serverNow ?? "");
  if (!Number.isFinite(at) || !Number.isFinite(now)) return false;
  const age = now - at;
  return age >= -RELOAD_CLOCK_SKEW_MS && age <= RELOAD_FRESH_MS;
}

/**
 * Milliseconds to wait before the next server check, given when the last one ran.
 * Events inside the window are coalesced into one trailing check rather than dropped, so a
 * stream of forged events cannot hide a real one.
 */
export function nextCheckDelay(lastCheckAt: number, now: number): number {
  if (!Number.isFinite(lastCheckAt) || lastCheckAt <= 0) return 0;
  // A tablet clock that jumped backwards leaves the stored time in the future; check now rather
  // than wait out the gap (phase 4 verifier note).
  if (lastCheckAt > now) return 0;
  return Math.max(0, lastCheckAt + RELOAD_CHECK_WINDOW_MS - now);
}
